import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useCurrentUser } from "./useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { userId } = useCurrentUser();
  const [favorites, setFavorites] = useState([]);

  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${BASE_URL}/api/favorites/${userId}`, { credentials: "include" });
      if (!res.ok) {
        console.error("fetchFavorites failed:", res.status, await res.text());
        return;
      }
      const data = await res.json();
      if (data.success) {
        setFavorites(data.data);
      } else {
        console.error("fetchFavorites returned success:false", data);
      }
    } catch (err) {
      console.error("FavoritesContext fetch error:", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const isFavorite = useCallback(
    (screenPath) => favorites.some((f) => f.screen_path === screenPath),
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (screenPath, screenName) => {
      if (!userId) return;
      const already = isFavorite(screenPath);

      // Optimistic update — both Sidebar and Dashboard see it instantly
      if (already) {
        setFavorites((prev) => prev.filter((f) => f.screen_path !== screenPath));
      } else {
        setFavorites((prev) => [...prev, { screen_path: screenPath, screen_name: screenName }]);
      }

      try {
        if (already) {
          const res = await fetch(
            `${BASE_URL}/api/favorites/${userId}/${encodeURIComponent(screenPath)}`,
            { method: "DELETE", credentials: "include" }
          );
          if (!res.ok) throw new Error("DELETE failed");
        } else {
          const res = await fetch(`${BASE_URL}/api/favorites`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userId, screen_path: screenPath, screen_name: screenName }),
          });
          if (!res.ok) throw new Error("POST failed");
        }
        // Sync with server AFTER the write has completed
        await fetchFavorites();
      } catch (err) {
        console.error("toggleFavorite error:", err);
        // Revert optimistic update on failure
        await fetchFavorites();
      }
    },
    [userId, isFavorite, fetchFavorites]
  );

  return (
    <FavoritesContext.Provider value={{ favorites, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavoritesContext must be used inside FavoritesProvider");
  return ctx;
};
